import { File, FileMode, Paths } from 'expo-file-system';
import type { ByteSink, ByteSource } from './source';

// The phone: the archive is a file the person picked in Files; an extracted
// entry is written to a temporary file in the app's cache, which the upload
// pipeline can send and the system may clear.

export function fileSource(file: File): ByteSource {
  const handle = file.open(FileMode.ReadOnly);
  const size = handle.size ?? file.size;
  return {
    size,
    async read(offset, length) {
      if (offset < 0 || length < 0 || offset + length > size) throw new RangeError('read outside the archive');
      handle.offset = offset;
      const bytes = handle.readBytes(length);
      if (bytes.length !== length) throw new RangeError('short read');
      return bytes;
    },
    close() { handle.close(); },
  };
}

export function cacheFileSink(extension: string): ByteSink<File> {
  const safe = /^[a-z0-9]{1,5}$/.test(extension) ? extension : 'bin';
  const file = new File(Paths.cache, `archive-${Date.now()}-${Math.random().toString(36).slice(2)}.${safe}`);
  file.create();
  const handle = file.open(FileMode.Truncate);
  let open = true;
  const close = () => { if (open) { handle.close(); open = false; } };
  return {
    write(chunk) { handle.writeBytes(chunk); },
    async finish() { close(); return file; },
    abort() { close(); try { file.delete(); } catch { /* already gone */ } },
  };
}

/**
 * The person's own choice of ZIP files in Files — several when Meta split the
 * download into parts; empty when they cancel.
 */
export async function pickZipFiles(): Promise<File[]> {
  const picked = await File.pickFileAsync({ mimeTypes: ['application/zip', 'application/x-zip-compressed'], multipleFiles: true });
  return picked.canceled ? [] : picked.result;
}
